import type {ReactNode} from 'react';
import {ArrowRight, CalendarDays, CheckCircle2, Clock3, Gamepad2, Users} from 'lucide-react';
import {EditorialAuthorLink} from './EditorialAuthor';

type PsPlusAugustNewsPageProps = {
  header: ReactNode;
  footer: ReactNode;
};

const officialAnnouncement =
  'https://blog.br.playstation.com/2026/07/28/jogos-mensais-de-agosto-para-assinantes-playstation-plus-dying-light-2-stay-human-reloaded-edition-big-walk-signalis/';
const officialGamesPage = 'https://www.playstation.com/pt-br/ps-plus/games/';
const officialSupport =
  'https://www.playstation.com/pt-br/support/subscriptions/subscribe-ps-plus-download-monthly-games/';

export function PsPlusAugustNewsPage({header, footer}: PsPlusAugustNewsPageProps) {
  return (
    <main className="site-shell article-shell news-article-shell">
      {header}
      <article className="ps5-article news-article">
        <header className="article-hero">
          <div className="article-hero-copy">
            <a className="breadcrumb" href="/noticias">GuiaSite / Notícias / Games</a>
            <p className="section-kicker">PlayStation Plus · Agosto de 2026</p>
            <h1>PS Plus de agosto de 2026: Dying Light 2, Big Walk e Signalis já estão disponíveis</h1>
            <p className="article-lead">
              Os três jogos mensais podem ser adicionados à biblioteca de 4 de agosto a 1º de
              setembro. A seleção cobre campanha longa, cooperação social e terror psicológico;
              este guia explica qual priorizar e como não perder o resgate.
            </p>
            <div className="article-meta">
              <EditorialAuthorLink />
              <span><time dateTime="2026-08-04">Publicado em 4 de agosto de 2026</time></span>
              <span>Leitura útil: 7 min</span>
            </div>
          </div>
          <figure className="article-hero-media news-hero-media">
            <img loading="eager"
              src="/guisite/news-ps-plus-agosto-2026-oficial.webp"
              alt="Arte oficial dos jogos mensais do PlayStation Plus de agosto de 2026: Dying Light 2, Big Walk e Signalis"
              width={2560}
              height={1440}
              fetchPriority="high"
              decoding="async"
            />
            <figcaption>Imagem oficial: Sony Interactive Entertainment / PlayStation Blog Brasil.</figcaption>
          </figure>
        </header>

        <div className="article-layout">
          <aside className="article-toc" aria-label="Índice da notícia">
            <strong>Nesta notícia</strong>
            <a href="#resumo-ps-plus-agosto">Resumo direto</a>
            <a href="#jogos-ps-plus-agosto">Jogos e plataformas</a>
            <a href="#qual-jogar-primeiro">Qual jogar primeiro</a>
            <a href="#como-resgatar">Como resgatar</a>
            <a href="#mensais-vs-catalogo">Mensais x catálogo</a>
            <a href="#marvel-tokon">Bônus de Marvel Tōkon</a>
            <a href="#perguntas-ps-plus-agosto">Perguntas frequentes</a>
            <a href="#fontes-ps-plus-agosto">Fontes</a>
          </aside>

          <div className="article-body">
            <section className="answer-card" id="resumo-ps-plus-agosto">
              <p className="section-kicker">Resposta direta</p>
              <h2>Adicione os três jogos à biblioteca, mesmo que você não pretenda baixá-los agora.</h2>
              <p>
                <strong>Dying Light 2 Stay Human</strong> é a escolha mais robusta para campanha,
                progressão e cooperação. <strong>Big Walk</strong> combina melhor com grupos que
                querem conversar e resolver desafios juntos. <strong>Signalis</strong> é a melhor
                opção para uma experiência solo, concentrada e atmosférica.
              </p>
              <p>
                A Sony confirma disponibilidade entre <strong>4 de agosto e 1º de setembro de
                2026</strong>. Os jogos mensais fazem parte dos planos Essential, Extra e Deluxe.
                Basta adicioná-los à biblioteca dentro do prazo; o download pode ficar para depois,
                desde que a assinatura esteja ativa quando você for jogar.
              </p>
              <div className="news-signal-row" aria-label="Informações essenciais">
                <span><CalendarDays size={18} /> Até 1º de setembro</span>
                <span><Gamepad2 size={18} /> PS5 e PS4</span>
                <span><CheckCircle2 size={18} /> Todos os planos</span>
              </div>
            </section>

            <section id="jogos-ps-plus-agosto">
              <p className="section-kicker">Seleção oficial</p>
              <h2>Quais são os jogos mensais do PS Plus em agosto de 2026?</h2>
              <div className="news-fact-table" role="table" aria-label="Jogos mensais do PlayStation Plus em agosto de 2026">
                <div role="row"><strong>Jogo</strong><strong>Plataforma</strong><strong>Melhor para</strong></div>
                <div role="row"><span>Dying Light 2 Stay Human</span><span>PS5 e PS4</span><span>Campanha, ação e cooperação</span></div>
                <div role="row"><span>Big Walk</span><span>PS5</span><span>Amigos, comunicação e enigmas</span></div>
                <div role="row"><span>Signalis</span><span>PS4</span><span>Terror psicológico e jogo solo</span></div>
              </div>
              <p className="source-note">
                A disponibilidade pode variar por região. Confirme a versão exibida na PlayStation
                Store brasileira antes de iniciar o download.
              </p>
            </section>

            <section id="qual-jogar-primeiro">
              <p className="section-kicker">Curadoria GuiaSite</p>
              <h2>Qual jogo vale priorizar?</h2>
              <div className="recommendation-grid news-pick-grid">
                <div>
                  <strong>Melhor pacote do mês</strong>
                  <p>
                    <b>Dying Light 2</b> oferece o maior volume de conteúdo e suporta cooperação
                    para até quatro jogadores. É a escolha mais segura para quem quer ocupar várias
                    semanas com campanha, exploração e evolução de personagem.
                  </p>
                </div>
                <div>
                  <strong>Melhor para jogar com amigos</strong>
                  <p>
                    <b>Big Walk</b> foi construído em torno de comunicação e trabalho em equipe. Faz
                    mais sentido para um grupo disposto a descobrir o jogo junto do que para quem
                    procura competição ou uma campanha tradicional.
                  </p>
                </div>
                <div>
                  <strong>Melhor para jogar sozinho</strong>
                  <p>
                    <b>Signalis</b> troca escala por direção artística, enigmas e tensão. É a
                    recomendação para quem prefere uma experiência mais autoral e não quer assumir
                    o compromisso de um mundo aberto.
                  </p>
                </div>
              </div>
              <div className="note-box">
                <Clock3 aria-hidden="true" />
                <div>
                  <strong>Tem pouco tempo?</strong>
                  <p>
                    Comece por Signalis. Se a prioridade for jogar em grupo, resgate Big Walk agora
                    e combine a sessão antes de instalar. Dying Light 2 recompensa uma rotina mais
                    contínua.
                  </p>
                </div>
              </div>
            </section>

            <section id="como-resgatar">
              <h2>Como resgatar os jogos sem perder o prazo</h2>
              <ol>
                <li>Abra a PlayStation Store no PS5, PS4, aplicativo ou site.</li>
                <li>Acesse a área do PlayStation Plus e selecione “Jogos mensais”.</li>
                <li>Adicione Dying Light 2, Big Walk e Signalis à sua biblioteca.</li>
                <li>Confirme que cada item aparece como adquirido; não é necessário baixar todos agora.</li>
              </ol>
              <p>
                A página de suporte da PlayStation informa que os jogos podem ser adicionados à
                biblioteca ou baixados durante o período de disponibilidade. Depois do resgate,
                eles permanecem vinculados à conta, mas exigem uma assinatura PlayStation Plus
                ativa para serem jogados.
              </p>
              <a className="text-link" href={officialSupport} target="_blank" rel="noopener noreferrer">
                Ver instruções oficiais da PlayStation <ArrowRight size={16} aria-hidden="true" />
              </a>
            </section>

            <section id="mensais-vs-catalogo">
              <h2>Jogos mensais não são a mesma coisa que o Catálogo de Jogos</h2>
              <p>
                Os <strong>jogos mensais</strong> podem ser resgatados por assinantes Essential,
                Extra ou Deluxe durante uma janela definida. Uma vez adicionados à biblioteca,
                continuam acessíveis enquanto a assinatura estiver ativa.
              </p>
              <p>
                O <strong>Catálogo de Jogos</strong>, por outro lado, é um benefício dos planos
                Extra e Deluxe. Os títulos entram e saem do catálogo e só podem ser jogados enquanto
                permanecerem nele. Essa diferença é importante: em agosto, não deixe os três jogos
                mensais apenas na lista de desejos; conclua o resgate.
              </p>
              <a className="text-link" href="/comparar/ps-plus-vs-game-pass/">
                Comparar PS Plus e Game Pass <ArrowRight size={16} aria-hidden="true" />
              </a>
            </section>

            <section id="marvel-tokon">
              <p className="section-kicker">Benefício adicional</p>
              <h2>O pacote de Marvel Tōkon começa em 6 de agosto</h2>
              <p>
                A Sony também anunciou um pacote do PlayStation Plus para <strong>MARVEL Tōkon:
                Fighting Souls</strong>, disponível a partir de 6 de agosto. O conteúdo inclui
                avatares e poses para a tela de resultados; não se trata de um quarto jogo mensal.
              </p>
              <p>
                Segundo o anúncio oficial, o pacote poderá ser resgatado até 6 de agosto de 2027.
                Trate-o como conteúdo adicional e não como parte da comparação entre os três jogos
                principais do mês.
              </p>
            </section>

            <section id="perguntas-ps-plus-agosto">
              <h2>Perguntas frequentes</h2>
              <div className="faq-list">
                <details>
                  <summary>Até quando posso resgatar os jogos do PS Plus de agosto?</summary>
                  <p>Dying Light 2, Big Walk e Signalis ficam disponíveis de 4 de agosto a 1º de setembro de 2026, conforme o anúncio da Sony.</p>
                </details>
                <details>
                  <summary>Preciso baixar os jogos antes de 1º de setembro?</summary>
                  <p>Não. Adicione-os à biblioteca dentro do prazo. O download pode ser feito depois, desde que sua assinatura PlayStation Plus esteja ativa.</p>
                </details>
                <details>
                  <summary>Os jogos estão disponíveis no PS4?</summary>
                  <p>Dying Light 2 e Signalis têm versão indicada para PS4. Big Walk foi anunciado para PS5.</p>
                </details>
                <details>
                  <summary>Quem tem PS Plus Essential recebe os três jogos?</summary>
                  <p>Sim. Jogos mensais fazem parte dos planos Essential, Extra e Deluxe. A disponibilidade final deve ser confirmada na loja da sua região.</p>
                </details>
              </div>
            </section>

            <section className="final-verdict">
              <p className="section-kicker">Conclusão GuiaSite</p>
              <h2>Agosto é um mês equilibrado, sem três jogos disputando exatamente o mesmo público.</h2>
              <p>
                Dying Light 2 entrega escala, Big Walk entrega convivência e Signalis entrega
                identidade. A decisão mais inteligente é resgatar todos hoje e instalar apenas o
                que combina com sua rotina. Assim você preserva a escolha sem transformar espaço
                de armazenamento em fila de culpa.
              </p>
            </section>

            <section id="fontes-ps-plus-agosto">
              <p className="section-kicker">Transparência editorial</p>
              <h2>Fontes e método</h2>
              <p>
                Esta notícia foi atualizada em 4 de agosto de 2026 com base em fontes primárias.
                Datas, plataformas e descrição dos benefícios vêm da Sony Interactive
                Entertainment. A priorização por perfil é uma análise editorial do GuiaSite.
              </p>
              <ul className="source-list">
                <li><a href={officialAnnouncement} target="_blank" rel="noopener noreferrer">PlayStation Blog Brasil: anúncio oficial dos jogos mensais de agosto</a></li>
                <li><a href={officialGamesPage} target="_blank" rel="noopener noreferrer">PlayStation: jogos e planos do PlayStation Plus</a></li>
                <li><a href={officialSupport} target="_blank" rel="noopener noreferrer">Suporte PlayStation: como acessar jogos mensais</a></li>
              </ul>
            </section>

            <section className="related-reading">
              <p className="section-kicker">Continue decidindo melhor</p>
              <h2>Leituras relacionadas</h2>
              <div className="recommendation-grid">
                <a href="/comparar/ps-plus-vs-game-pass/">
                  <strong>PS Plus ou Game Pass?</strong>
                  <p>Compare planos, catálogo e valor conforme sua rotina.</p>
                </a>
                <a href="/games/playstation-5/">
                  <strong>PS5 ainda vale a pena?</strong>
                  <p>Vida útil, biblioteca e o impacto de esperar pelo PS6.</p>
                </a>
                <a href="/noticias/ps-plus-julho-2026-catalogo-jogos-datas">
                  <strong>Catálogo do PS Plus de julho</strong>
                  <p>Entenda o que entrou no Extra e no Deluxe.</p>
                </a>
              </div>
            </section>
          </div>
        </div>
      </article>
      {footer}
    </main>
  );
}


